/**
 * Is another tab of this app open right now?
 *
 * The sweep is safe at start-up because *this* session has no undo history —
 * but undo history is per tab, not per origin. Delete an image in one tab, let
 * autosave persist it, open a second: the second tab's sweep reclaims the
 * bytes and the first tab's Ctrl+Z restores a node pointing at nothing. The
 * grace period does not help, because it measures when the asset was *stored*,
 * not when the reference was dropped.
 *
 * So the sweep asks first, and skips when anyone answers. Reclaiming a few
 * megabytes can always wait for a session where nobody else is editing.
 */

const CHANNEL = 'burfy-docs-presence';

/**
 * This tab's identity, so it can ignore its own replies.
 *
 * `BroadcastChannel` does not deliver a message back to the object that sent
 * it — but it does deliver to *every other* object on the same channel,
 * including ones in the same page. So the asker heard the announcer sitting a
 * few lines away, concluded another tab was open, and the sweep never ran
 * once. It passed locally and was caught by the browser specs against the
 * deployed site, which is the only place it was ever going to show up.
 */
const TAB_ID = globalThis.crypto?.randomUUID?.() ?? String(Math.random());

interface Presence {
  kind: 'ping' | 'pong';
  from: string;
}

const isPresence = (value: unknown): value is Presence =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Presence).from === 'string';

/** How long to wait for an answer. Long enough to cross a message queue. */
const LISTEN_MS = 250;

/**
 * Answer other tabs' questions for as long as this one is open.
 *
 * Returns a teardown, and does nothing where `BroadcastChannel` is missing —
 * jsdom, and older Safari.
 */
export function announcePresence(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;

  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event: MessageEvent) => {
    const message: unknown = event.data;
    if (!isPresence(message) || message.kind !== 'ping') return;
    // Never answer this tab's own question.
    if (message.from === TAB_ID) return;
    channel.postMessage({ kind: 'pong', from: TAB_ID } satisfies Presence);
  };

  return () => {
    channel.onmessage = null;
    channel.close();
  };
}

export function anotherTabIsOpen(timeoutMs = LISTEN_MS): Promise<boolean> {
  // No channel means no way to ask. Answering "yes" would disable the sweep
  // for ever in that browser; answering "no" risks the race above only when a
  // second tab is genuinely open, which is the same position as before this
  // existed.
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL);
    let settled = false;

    const finish = (answer: boolean) => {
      if (settled) return;
      settled = true;
      channel.onmessage = null;
      channel.close();
      resolve(answer);
    };

    channel.onmessage = (event: MessageEvent) => {
      const message: unknown = event.data;
      if (!isPresence(message) || message.kind !== 'pong') return;
      if (message.from === TAB_ID) return;
      finish(true);
    };

    channel.postMessage({ kind: 'ping', from: TAB_ID } satisfies Presence);
    setTimeout(() => finish(false), timeoutMs);
  });
}
